import React, { useState } from 'react';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import Plus from '@images/plus.svg';

interface Props {
  items?: {
    icon: React.FunctionComponent<React.SVGAttributes<SVGElement>>;
    text: string;
    onClick: () => void;
  }[];
  backgroundColor?: string;
  borderRadius?: string;
}

const ExpandableFAB: React.FC<Props> = ({ items, backgroundColor = Colors.cream, borderRadius = '2px' }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Container>
        <OptionsWrap $isOpen={isOpen} $backgroundColor={backgroundColor} $borderRadius={borderRadius}>
          {items?.map((item, index) => (
            <ItemButton key={index} onClick={() => item.onClick()}>
              <div style={{ display: 'flex', marginRight: '10px', alignItems: 'center' }}>{item.icon && <item.icon />}</div>
              <TextWrap>{item.text}</TextWrap>
            </ItemButton>
          ))}
        </OptionsWrap>
        <BottomRightFiller $isOpen={isOpen} />
        <Fab onClick={() => setIsOpen(!isOpen)} $isOpen={isOpen}>
          <StyledPlus />
        </Fab>
      </Container>
      <Backdrop $isOpen={isOpen} />
    </>
  );
};

export default ExpandableFAB;

interface StyleProps {
  $isOpen?: boolean;
  $backgroundColor?: string;
  $borderRadius?: string;
}

const StyledPlus = styled(Plus)`
  path {
    fill: ${Colors.cream};
  }
`;

const BottomRightFiller = styled.div<StyleProps>`
  background: ${Colors.cream};
  transition: all 0.2s;
  height: ${(props) => (props.$isOpen ? '32px' : '0')};
  width: ${(props) => (props.$isOpen ? '32px' : '0')};
  opacity: ${(props) => (props.$isOpen ? '1' : '0')};
  position: absolute;
  bottom: 32px;
  z-index: -1;
`;

const Container = styled.div`
  position: fixed;
  bottom: 24px;
  right: 86px;
  z-index: 999;
  display: flex;
  flex-direction: column;
  align-items: end;
`;

const Fab = styled.button<StyleProps>`
  all: unset;
  display: flex;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  border-radius: 100%;
  background: ${(props) => (props.$isOpen ? Colors.gold : Colors.lilac)};
  transition: background 0.3s;
  width: 48px;
  height: 48px;

  svg {
    height: 50%;
    width: 50%;
    transform: ${(props) => props.$isOpen && 'rotate(45deg)'};
    transition: transform 0.3s;
  }
`;

const OptionsWrap = styled.div<StyleProps>`
  display: flex;
  position: relative;
  z-index: -1;
  width: ${(props) => (props.$isOpen ? '266px' : '0')};
  height: ${(props) => (props.$isOpen ? '100%' : '0')};
  background: ${(props) => props.$backgroundColor};
  border-radius: ${(props) => props.$borderRadius};
  opacity: ${(props) => (props.$isOpen ? '1' : '0')};
  flex-direction: column;
  bottom: ${(props) => (props.$isOpen ? '0' : '-32px')};
  transition: all ease-in-out 0.4s;
  overflow: hidden;

  &:hover > button {
    opacity: 0.6;
  }

  & > button:hover {
    opacity: 1;
  }
`;

const ItemButton = styled.button`
  background: 0;
  border: 0;
  padding: 15px 20px;
  display: flex;
  padding-inline: 20px;
  align-items: center;
  border-bottom: 1px solid ${Colors.ecru};
  transition: all ease-in-out 0.3s;
  min-width: 260px;

  &:last-child {
    border-bottom-width: 0;
  }
`;

const TextWrap = styled.p`
  font-family: BrownStd;
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  text-align: left;
  color: ${Colors.navyBlue};
`;

const Backdrop = styled.div<StyleProps>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: ${Colors.navyBlue}25;
  z-index: 998;
  transition: all ease-in-out 0.3s;
  opacity: ${(props) => (props.$isOpen ? '1' : '0')};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
`;
